<template>
  <div class="container-fluid">
    <div class="container">
      <div class="header columns">
        <h2>Email Report Builder</h2>
        <div class="control-bar">
          <router-link
            :to="{ name: 'pdf_report_settings', params: { report_id: 'new' } }">
            <b-button
              type="is-success is-light"
              class="action-btn"
              icon-left="plus">
              New Report
            </b-button>
          </router-link>
        </div>
      </div>
      <div>
        <div class="columns is-vcentered report-table-filters">
          <div class="column pdf-search-input m-0 p-0">
            <b-input
              v-model="reportSearchInput"
              custom-class="no-border"
              placeholder="Search..."
              type="search"
              icon="magnify"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false" />
          </div>

          <!-- Widens the list rather than swapping it: live reports stay put
               and archived ones join them, tagged. The API decides which
               reports come back, so this is a reload, not a local filter. -->
          <div class="column is-narrow archived-toggle m-0 p-0">
            <b-switch
              v-model="showArchived"
              size="is-small"
              @input="loadAllPdfReports">
              Show archived
            </b-switch>
          </div>
        </div>
      </div>
      <b-table
        :data="resultsToShow"
        :loading="isLoading"
        :hoverable="true"
        class="pdf-reports-table">
        <b-table-column
          v-slot="props"
          sortable
          field="name"
          label="Name">
          <div class="name-cell">
            <span>{{ props.row.name }}</span>

            <b-tag
              v-if="isArchived(props.row)"
              class="archived-tag"
              type="is-warning"
              size="is-small"
              rounded>
              Archived
            </b-tag>

            <b-tooltip
              v-if="usageFor(props.row)"
              type="is-light"
              position="is-right"
              size="is-large"
              multilined>
              <b-tag
                class="live-tag"
                :type="usageFor(props.row).active_email_count ? 'is-success' : 'is-light'"
                size="is-small"
                rounded>
                {{ statusLabel(props.row) }}
              </b-tag>

              <template #content>
                <div class="live-tooltip">
                  <p>
                    This report is currently attached to the following
                    reports:
                  </p>

                  <ul v-if="usageFor(props.row).emails.length">
                    <li
                      v-for="email in usageFor(props.row).emails"
                      :key="email.email_report_id">
                      {{ emailSummary(email) }}
                    </li>
                  </ul>
                  <p
                    v-else-if="!emailDetailsVisible"
                    class="detail-note">
                    Ask someone with email access for the details.
                  </p>
                </div>
              </template>
            </b-tooltip>
          </div>
        </b-table-column>

        <!-- <b-table-column v-slot="props"
                        field="reports"
                        label="Reports">
          <b-tag
            class="data-tags">
            {{ props.row.moduleIds.length }}
          </b-tag>
        </b-table-column> -->

        <b-table-column
          v-slot="props"
          field="Actions"
          width="120"
          label="Actions">
          <div class="actions">
            <b-button
              v-if="isArchived(props.row)"
              class="action-btn"
              icon-right="archive-arrow-up"
              title="Restore this report to the library"
              @click="unarchiveReport(props.row)" />
            <b-button
              v-else
              class="action-btn"
              icon-right="archive-arrow-down"
              title="Archive this report"
              @click="confirmArchive(props.row)" />
            <b-button
              class="action-btn"
              icon-right="content-copy"
              @click="confirmDuplicate(props.row)" />
            <router-link
              :to="{
                name: 'pdf_report_editor',
                params: { report_id: props.row.key },
              }">
              <b-button
                type="is-primary"
                class="action-btn"
                icon-right="lead-pencil" />
            </router-link>
          </div>
        </b-table-column>

        <template #empty>
          <div class="has-text-centered mt-5 mb-5">
            <b-tag
              v-if="!isLoading"
              class="data-tags"
              rounded
              size="is-medium">
              No Reports Found 🥥
            </b-tag>
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
  import FuzzySearch from 'fuzzy-search'

  const IN_USE_LIST_STYLE =
    'margin-top: 8px; padding-left: 18px; list-style: disc; text-align: left;'

  export default {
    name: 'PdfReportLibraryPage',
    data () {
      return {
        allPdfReports: [],
        emailUsageByKey: {},
        emailDetailsVisible: false,
        isLoading: false,
        reportSearchInput: '',
        reportsFilterByActiveTabs: 0,
        showArchived: false,
        // Keys the API flagged as archived in the list it just returned. The
        // reports themselves do not carry the flag, and with both halves in
        // one list their position no longer implies it.
        archivedReportKeys: [],
      }
    },

    computed: {
      resultsToShow () {
        if (this.reportSearchInput) {
          return this.searcher.search(this.reportSearchInput)
        }
        return this.allPdfReports
      },

      searcher () {
        const searcher = new FuzzySearch(this.allPdfReports, ['name',])
        return searcher
      },
    },

    async mounted () {
      this.loadAllPdfReports()
    },

    methods: {
      usageFor (report) {
        return this.emailUsageByKey[report.key] || null
      },

      isArchived (report) {
        return this.archivedReportKeys.includes(report.key)
      },

      statusLabel (report) {
        const usage = this.usageFor(report)
        if (!usage) return ''
        return usage.active_email_count ? 'LIVE' : 'PAUSED'
      },

      emailSummary (email) {
        const isLive = email.is_active && email.attachment_is_active
        const parts = [email.name || 'Untitled email',]
        if (email.cadence && email.cadence.type) {
          parts.push(String(email.cadence.type).toLowerCase())
        }
        parts.push(isLive ? 'active' : 'paused')
        return parts.join(' · ')
      },

      async loadAllPdfReports () {
        this.isLoading = true
        const response = await this.$apis.pdfReports.getAllPdfReportsWithUsage({
          includeArchived: this.showArchived,
        })

        // Email usage comes back with the list, so the live indicator costs
        // no extra request. Archived reports keep theirs: an archived report
        // can still be attached to a paused email, and the archive would be
        // misleading if it looked inert.
        this.allPdfReports = response ? response.reports : []
        this.emailUsageByKey = response ? response.email_usage || {} : {}
        this.emailDetailsVisible = !!(response && response.email_details_visible)
        this.archivedReportKeys = response
          ? response.archived_report_keys || []
          : []

        this.isLoading = false
      },

      confirmArchive (report) {
        this.$buefy.dialog.confirm({
          title: 'Archive Report',
          message:
            `Archive "${this.escapeHtml(report.name)}"? It leaves the library `
            + 'but keeps every email attachment and send record it already '
            + 'has, and you can restore it at any time.',
          confirmText: 'Archive',
          onConfirm: () => this.archiveReport(report),
        })
      },

      async archiveReport (report, force = false) {
        this.isLoading = true
        const response = await this.$apis.pdfReports.archivePdfReport(
          report.key,
          { force, }
        )
        this.isLoading = false

        if (response && response.conflict) {
          this.showInUseDialog(report, response.conflict)
          return
        }
        if (!response) return

        this.$buefy.toast.open({
          message: `"${report.name}" archived`,
          type: 'is-success',
        })
        await this.loadAllPdfReports()
      },

      // The API refuses to archive a report an active email still carries,
      // because archiving is invisible from the email's side: nothing about
      // its schedule changes, so its next send would quietly produce
      // something different. Name the emails and let the person decide,
      // rather than showing them a conflict they cannot act on.
      showInUseDialog (report, conflict) {
        const references = conflict.references || []
        let message

        if (references.length) {
          const items = references
            .map((reference) => `<li>${this.escapeHtml(this.emailSummary(reference))}</li>`)
            .join('')
          message =
            `<p>"${this.escapeHtml(report.name)}" is still attached to these `
            + 'active email reports, and archiving it would change what their '
            + 'next scheduled send produces.</p>'
            // Buefy renders the dialog at the body, out of reach of this
            // component's scoped styles, so the list carries its own.
            + `<ul style="${IN_USE_LIST_STYLE}">${items}</ul>`
        } else {
          // Without email permission the API withholds the names, and its
          // message is the only thing that knows who to ask.
          message = `<p>${this.escapeHtml(conflict.message)}</p>`
        }

        this.$buefy.dialog.confirm({
          title: 'Report Is Still Sending',
          message,
          confirmText: 'Archive Anyway',
          cancelText: 'Leave It',
          type: 'is-warning',
          hasIcon: true,
          onConfirm: () => this.archiveReport(report, true),
        })
      },

      async unarchiveReport (report) {
        this.isLoading = true
        const response = await this.$apis.pdfReports.unarchivePdfReport(
          report.key
        )
        this.isLoading = false
        if (!response) return

        this.$buefy.toast.open({
          message: `"${report.name}" restored to the library`,
          type: 'is-success',
        })
        await this.loadAllPdfReports()
      },

      // Buefy renders a dialog message as HTML, so anything a customer named
      // goes through here first.
      escapeHtml (value) {
        return String(value == null ? '' : value).replace(
          /[&<>"']/g,
          (character) => ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            '\'': '&#39;',
          }[character])
        )
      },

      async confirmDuplicate (report) {
        this.$buefy.dialog.confirm({
          title: 'Duplicate Report',
          message: 'Are you sure you want to duplicate this report?',
          confirmText: 'Duplicate',
          onConfirm: () => this.duplicateReport(report),
        })
      },

      async duplicateReport (report) {
        this.isLoading = true

        const response = await this.$apis.pdfReports.duplicatePdfReport(
          report.key,
          { name: `${report.name} - Copy`, }
        )

        this.$router.push({
          name: 'pdf_report_editor',
          params: { report_id: response.key, },
        })

        this.isLoading = false
      },
    },
  }
</script>

<style lang="scss" scoped>
.container-fluid {
  background: white;
  width: 100%;
  padding: 20px 14px;
  overflow: auto;

  .header {
    margin-top: 30px;

    padding-bottom: 20px;

    h2 {
      font-weight: 400;
      font-size: 22px;
    }

    .control-bar {
      display: flex;
      margin-left: auto;
      margin-right: 0;
    }
  }

  .pdf-reports-table {
    font-size: 14px;
    margin-top: 20px;
    border: $border-1;
    border-radius: 6px;
  }

  .report-table-filters {
    padding: 8px 8px;
    border: $border-1;
    border-radius: 6px;
    margin: 0px;
  }

  .archived-tag {
    margin-left: 6px;
  }

  .archived-toggle {
    display: flex;
    align-items: center;
    white-space: nowrap;
    padding-right: 8px !important;
  }

  .data-tags {
    font-size: 12px;
    margin: 2px;
  }

  .name-cell {
    display: flex;
    align-items: flex-start;
    gap: 8px;

    // Buefy's .b-tooltip is display: flex, so .tooltip-trigger stretches to
    // the full line box, and the chip -- being inline-flex -- is then
    // baseline-aligned inside it, which pins the chip's bottom to the bottom
    // of the line no matter what margin it carries. Making the trigger a
    // flex container lays the chip out as a flex item instead, so the
    // margin-top below translates 1:1 into a lift.
    ::v-deep .tooltip-trigger {
      display: flex;
      align-items: flex-start;
    }
  }

  .live-tag {
    font-weight: 600;
    letter-spacing: 0.02em;

    // Bulma pins .tag at height: 2em (24px), which overhangs the name's
    // 21px line box; let the box hug its own label instead (~18px tall).
    height: auto;
    padding: 1px 8px;
    line-height: 1.35;

    // Lit-indicator glow, live reports only.
    &.is-success {
      box-shadow: 0 0 3px 1px rgba(72, 199, 116, 0.55);
    }
  }

  .live-tooltip {
    text-align: left;

    ul {
      margin-top: 6px;
      list-style: disc;
      padding-left: 16px;
    }

    .detail-note {
      margin-top: 6px;
      opacity: 0.7;
    }
  }

  .actions {
    display: flex;

    .action-btn {
      margin: 0 5px;
    }
  }
}
</style>
